/**
 * Coming up: due items under Overdue and Upcoming headings, eight at a time,
 * with an optional milestones switch. The caller builds the items with
 * buildActionItems and decides where a press goes.
 */
import React from 'react';
import { Pressable, StyleSheet, Text, View, useWindowDimensions } from 'react-native';
import type { ActionItem } from '../../features/dashboard/actionQueueItems';
import { useSharedPalette } from '../theme';
import { comingUpDueCue, comingUpSections } from './homeModels';

export interface ComingUpListProps {
  items: ActionItem[];
  pending?:boolean;
  windowDays?: number;
  maxRows?: number;
  compact?: boolean;
  onOpen?: (item: ActionItem) => void;
  milestones?: { shown: boolean; onToggle: () => void };
}

export function ComingUpList({ items, windowDays = 7, maxRows = 8, onOpen, milestones,pending=false,compact=false }: ComingUpListProps) {
  const palette = useSharedPalette();
  const largeText=useWindowDimensions().fontScale>1.2;
  const [expanded, setExpanded] = React.useState(false);
  const today = new Date();
  const visible = expanded ? items : items.slice(0, maxRows);
  const overflow = items.length - maxRows;
  const tone = { danger: palette.danger, warning: palette.warning, muted: palette.muted };

  const footerButton = (label: string, onPress: () => void, extra?: object) => (
    <Pressable onPress={onPress} style={[styles.footer, compact && styles.compactFooter, { borderTopColor: palette.border }]} {...extra}>
      {({ pressed }) => <Text style={[styles.footerText, { color: palette.muted, opacity: pressed ? 0.6 : 1 }]}>{label}</Text>}
    </Pressable>
  );
  const milestoneSwitch = milestones && footerButton(
    milestones.shown ? 'Hide project milestones' : 'Include project milestones',
    milestones.onToggle,
    { accessibilityRole: 'switch', accessibilityState: { checked: milestones.shown } },
  );

  if(pending)return <View>
    <Text style={[styles.heading,{color:palette.muted}]} accessibilityRole="header">UPCOMING</Text>
    {milestoneSwitch}
  </View>;

  if (visible.length === 0) {
    return (
      <View>
        <Text style={[styles.empty, { color: palette.muted }]}>Nothing due in the next {windowDays} days.</Text>
        {milestoneSwitch}
      </View>
    );
  }

  return (
    <View>
      {comingUpSections(visible).map((section) => (
        <View key={section.key}>
          <Text
            style={[styles.heading, { color: section.key === 'overdue' ? palette.danger : palette.muted }]}
            accessibilityRole="header"
          >
            {section.label.toUpperCase()}
          </Text>
          {section.rows.map((item, index) => {
            const due = comingUpDueCue(item, today);
            const row = (
              <View
                style={[
                  styles.row,
                  compact && styles.compactRow,
                  largeText&&styles.largeRow,
                  index < section.rows.length - 1 && { borderBottomWidth: StyleSheet.hairlineWidth, borderBottomColor: palette.border },
                ]}
              >
                <View style={styles.text}>
                  <Text style={[styles.title, { color: palette.foreground }]} numberOfLines={largeText?undefined:1}>{item.title}</Text>
                  <Text style={[styles.context, { color: palette.muted }]} numberOfLines={largeText?undefined:1}>{item.context}</Text>
                </View>
                <Text style={[styles.due, { color: tone[due.tone] }]}>{due.label}</Text>
              </View>
            );
            return onOpen && item.target ? (
              <Pressable key={item.id} onPress={() => onOpen(item)} accessibilityRole="button">{row}</Pressable>
            ) : (
              <View key={item.id}>{row}</View>
            );
          })}
        </View>
      ))}
      {overflow > 0 && footerButton(
        expanded ? 'Show fewer items' : `Show ${overflow} more due`,
        () => setExpanded((value) => !value),
        { accessibilityRole: 'button', accessibilityState: { expanded } },
      )}
      {milestoneSwitch}
    </View>
  );
}

const styles = StyleSheet.create({
  compactRow: {minHeight: 48, paddingVertical: 8},
  compactFooter: {minHeight: 36},
  empty: { fontSize: 12, textAlign: 'center', paddingVertical: 24, paddingHorizontal: 12 },
  heading: { fontSize: 12, fontWeight: '500', letterSpacing: 1.4, paddingHorizontal: 12, paddingTop: 12, paddingBottom: 6 },
  row: { flexDirection: 'row', alignItems: 'center', gap: 12, minHeight: 48, paddingHorizontal: 12, paddingVertical: 10 },
  largeRow:{flexDirection:'column',alignItems:'flex-start',gap:4},
  text: { flex: 1, minWidth: 0 },
  title: { fontSize: 14, fontWeight: '500' },
  context: { fontSize: 12, marginTop: 2 },
  due: { fontSize: 12, flexShrink: 0 },
  footer: { minHeight: 44, justifyContent: 'center', paddingHorizontal: 12, borderTopWidth: StyleSheet.hairlineWidth },
  footerText: { fontSize: 14 },
});
